import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField, Box, Typography, LinearProgress } from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/styles';
import zxcvbn from 'zxcvbn';

const useStyles = makeStyles((theme) => ({
  strengthContainer: {
    marginTop: theme.spacing(1)
  },
  strengthBar: {
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[300]
  },
  strengthLabel: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    fontWeight: 500
  },
  feedback: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem'
  }
}));

const ColoredLinearProgress = withStyles({
  bar: {
    backgroundColor: (props) => props.barcolor
  }
})(LinearProgress);

const strengthConfig = {
  0: { label: 'Very Weak', color: '#f44336' }, // red
  1: { label: 'Weak', color: '#ff9800' }, // orange
  2: { label: 'Fair', color: '#ffc107' }, // yellow
  3: { label: 'Good', color: '#8bc34a' }, // light green
  4: { label: 'Strong', color: '#4caf50' } // green
};

const PasswordFieldWithStrength = ({
  value,
  onChange,
  error,
  helperText,
  className,
  ...otherProps
}) => {
  const classes = useStyles();
  const [strength, setStrength] = useState(null);

  useEffect(() => {
    if (value && value.length > 0) {
      const result = zxcvbn(value);
      setStrength(result);
    } else {
      setStrength(null);
    }
  }, [value]);

  const getStrengthPercentage = () => {
    if (!strength) return 0;
    return ((strength.score + 1) / 5) * 100;
  };

  const getStrengthColor = () => {
    if (!strength) return strengthConfig[0].color;
    return strengthConfig[strength.score].color;
  };

  const getStrengthLabel = () => {
    if (!strength) return '';
    return strengthConfig[strength.score].label;
  };

  const getFeedbackMessage = () => {
    if (!strength || !value) return '';

    const messages = [];

    // Add warning if present
    if (strength.feedback.warning) {
      messages.push(strength.feedback.warning);
    }

    // Add suggestions if present
    if (strength.feedback.suggestions && strength.feedback.suggestions.length > 0) {
      messages.push(...strength.feedback.suggestions);
    }

    return messages.join('. ');
  };

  return (
    <Box>
      <TextField
        {...otherProps}
        className={className}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        type="password"
        autoComplete="new-password"
      />
      {value && value.length > 0 && (
        <Box className={classes.strengthContainer}>
          <ColoredLinearProgress
            variant="determinate"
            value={getStrengthPercentage()}
            className={classes.strengthBar}
            barcolor={getStrengthColor()}
          />
          <Typography
            className={classes.strengthLabel}
            style={{ color: getStrengthColor() }}>
            Password Strength: {getStrengthLabel()}
          </Typography>
          {getFeedbackMessage() && (
            <Typography
              className={classes.feedback}
              color="textSecondary">
              {getFeedbackMessage()}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

PasswordFieldWithStrength.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  className: PropTypes.string
};

PasswordFieldWithStrength.defaultProps = {
  value: '',
  error: false,
  helperText: null,
  className: ''
};

export default PasswordFieldWithStrength;
